import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import type { FC } from "react";
import { useCallback, useState } from "react";
import CommunicationTextField from "../../../../components/StaffCommunication/util/CommunicationTextField";
import SystemButton from "../../../../components/UIToolkit/SystemButton/SystemButton";
import { useFormatPersonName } from "../../../../hooks/useNameFormat";
import { INK } from "../../../../theme/tokens";
import type { Nurse } from "../../../../utils/models";

type Props = {
	open: boolean;
	onClose: () => void;
	nurse: Nurse;
};

enum DialogStep {
	NewStaffMessage = "NEW_STAFF",
	TeamMessage = "TEAM",
	ShiftManagerMessage = "SHIFT_MANAGER",
}

/**
 * Open dialog to send first shift messages to new staff team, and shift manager
 *
 * TODO: Refactor this component to use a more generic multi-step dialog approach when we should make this production delivery,
 * 		this was done quickly to meet a deadline for a DEMO.
 */
export const FirstShiftDialog: FC<Props> = ({ open, onClose, nurse }) => {
	const formatPersonName = useFormatPersonName();
	const [currentStep, setCurrentStep] = useState<DialogStep>(DialogStep.NewStaffMessage);
	const [newStaffMessage, setNewStaffMessage] = useState(
		`Hi ${nurse.firstName}! Just a reminder about your first shift 😊\n📍 Location: Main St\n👩‍⚕️ Shift Charge: Dianna\n📞 Dianna's phone: (555) 555-555-55\nWe're really excited to have you on board and part of the team, and we're looking forward to working with you!`,
	);
	const [teamMessage, setTeamMessage] = useState(
		`Hi everyone! 👋\nJust a reminder that ${nurse.firstName}, our new ${nurse.role}, is joining the shift today.\n\nFirst shifts can be tough, so please don't forget to say hello and be extra welcoming.\nThanks for helping make their first day a great one! 😊`,
	);
	const [shiftManagerMessage, setShiftManagerMessage] = useState(
		`Hi Diana! Just a quick heads-up that ${nurse.firstName}, our new ${nurse.role}, will be starting their first shift today. Please make sure to give them a warm welcome and help them get settled in. Let me know if you need any assistance or have any questions. Thanks for your support!`,
	);

	const handleBack = useCallback(() => {
		if (currentStep === DialogStep.TeamMessage) {
			// Back to NewStaffMessage
			setCurrentStep(DialogStep.NewStaffMessage);
		}
		if (currentStep === DialogStep.ShiftManagerMessage) {
			// Back to TeamMessage
			setCurrentStep(DialogStep.TeamMessage);
		}
	}, [currentStep]);

	const handleSkip = useCallback(() => {
		if (currentStep === DialogStep.NewStaffMessage) {
			// Skip to team message
			setCurrentStep(DialogStep.TeamMessage);
		} else if (currentStep === DialogStep.TeamMessage) {
			// Skip to shift manager message
			setCurrentStep(DialogStep.ShiftManagerMessage);
		} else if (currentStep === DialogStep.ShiftManagerMessage) {
			// Close dialog completely
			onClose();
		}
	}, [currentStep, onClose]);

	const handleSendNow = useCallback(() => {
		const message = currentStep === DialogStep.NewStaffMessage ? newStaffMessage : teamMessage;
		console.log(`Send Now - ${currentStep}:`, message);

		if (currentStep === DialogStep.NewStaffMessage) {
			// Move to team message
			setCurrentStep(DialogStep.TeamMessage);
		} else if (currentStep === DialogStep.TeamMessage) {
			// move to shift manager message
			setCurrentStep(DialogStep.ShiftManagerMessage);
		} else if (currentStep === DialogStep.ShiftManagerMessage) {
			// Close dialog completely
			onClose();
		}
	}, [currentStep, newStaffMessage, teamMessage, onClose]);

	const handleSend12hBefore = useCallback(() => {
		const message = currentStep === DialogStep.NewStaffMessage ? newStaffMessage : teamMessage;
		console.log(`Send 12h Before - ${currentStep}:`, message);

		if (currentStep === DialogStep.NewStaffMessage) {
			// Move to team message
			setCurrentStep(DialogStep.TeamMessage);
		} else if (currentStep === DialogStep.TeamMessage) {
			// move to shift manager message
			setCurrentStep(DialogStep.ShiftManagerMessage);
		} else if (currentStep === DialogStep.ShiftManagerMessage) {
			// Close dialog completely
			onClose();
		}
	}, [currentStep, newStaffMessage, teamMessage, onClose]);

	const handleMessageChange = useCallback(
		(value: string) => {
			if (currentStep === DialogStep.NewStaffMessage) {
				setNewStaffMessage(value);
			} else if (currentStep === DialogStep.TeamMessage) {
				setTeamMessage(value);
			} else if (currentStep === DialogStep.ShiftManagerMessage) {
				setShiftManagerMessage(value);
			}
		},
		[currentStep],
	);

	const handleEnter = useCallback(() => {
		// Enter key doesn't do anything specific in this case
	}, []);

	const currentMessage =
		currentStep === DialogStep.NewStaffMessage
			? newStaffMessage
			: currentStep === DialogStep.TeamMessage
				? teamMessage
				: currentStep === DialogStep.ShiftManagerMessage
					? shiftManagerMessage
					: "";
	const title =
		currentStep === DialogStep.NewStaffMessage
			? "Message to New Staff Member"
			: currentStep === DialogStep.TeamMessage
				? "Message to Team Members"
				: currentStep === DialogStep.ShiftManagerMessage
					? "Message to Shift Manager"
					: "";

	return (
		<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
			<DialogTitle color={INK.SECONDARY}>{title}</DialogTitle>
			<DialogContent>
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						gap: 2,
						pt: 1,
					}}
				>
					<Typography
						variant="body2"
						sx={{
							color: INK.SECONDARY,
						}}
					>
						{currentStep === DialogStep.NewStaffMessage
							? `This message will be sent to ${formatPersonName(nurse)} about their first shift.`
							: currentStep === DialogStep.TeamMessage
								? "This message will be sent to all team members working on this shift."
								: currentStep === DialogStep.ShiftManagerMessage
									? "This message will be sent to shift manager"
									: ""}
					</Typography>
					<CommunicationTextField placeholder="Type your message" value={currentMessage} onChange={handleMessageChange} onEnter={handleEnter} maxLines={8} />
				</Box>
			</DialogContent>
			<DialogActions>
				<Box
					sx={{
						display: "flex",
						gap: 1,
						p: 1,
					}}
				>
					{currentStep !== DialogStep.NewStaffMessage && (
						<SystemButton onClick={handleBack} buttonVariant="borderless" sx={{ fontSize: "14px" }}>
							previous
						</SystemButton>
					)}
					<SystemButton onClick={handleSkip} buttonVariant="borderless" sx={{ fontSize: "14px" }}>
						Skip
					</SystemButton>
					<SystemButton onClick={handleSend12hBefore} buttonVariant="secondary" sx={{ fontSize: "14px" }}>
						Send 12h Before
					</SystemButton>
					<SystemButton onClick={handleSendNow} buttonVariant="primary" sx={{ fontSize: "14px" }}>
						Send Now
					</SystemButton>
				</Box>
			</DialogActions>
		</Dialog>
	);
};
